import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet, Text, ScrollView, Animated, Easing } from "react-native";
import WorkspaceCard, { WorkspaceCardProps } from "../WorkspaceCard/WorkspaceCard";
import { colors, spacing, webTransitions } from "../../../styles/design-system";
import { AxioLogo } from "../../svgs/logos";
import { useWorkspaceThreads } from "../../../contexts/WorkspaceThreadsContext";
import { PanelLeft, PanelLeftClose, X } from "lucide-react";
import Tooltip from "../../common/tooltip/Tooltip";
import { useAppConfig } from "../../../AppConfigProvider";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import { homeWorkspace } from "../../../hooks/useWorkspaceLists";

export interface WorkspaceSidebarWorkspace extends Omit<WorkspaceCardProps, "onPress" | "onThreadsPress"> {
  id: string;
}

export interface WorkspaceSidebarFooterItem {
  id: string;
  label: string;
}

export interface WorkspaceSidebarProps {
  generalWorkspaces: WorkspaceSidebarWorkspace[];
  courseWorkspaces: WorkspaceSidebarWorkspace[];
  todoWorkspaces: WorkspaceSidebarWorkspace[];
  settingsWorkspaces: WorkspaceSidebarWorkspace[];
  activeWorkspaceId?: string;
  onWorkspacePress?: (id: string) => void;
  onWorkspaceThreadsPress?: (id: string) => void;
}

export const EXPANDED_WIDTH = 272;
export const COLLAPSED_WIDTH = 67;

const WorkspaceSidebar: React.FC<WorkspaceSidebarProps> = ({
  generalWorkspaces,
  courseWorkspaces,
  todoWorkspaces,
  settingsWorkspaces,
  activeWorkspaceId,
  onWorkspacePress,
  onWorkspaceThreadsPress,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const { isLarge } = useResponsiveScreen();
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useWorkspaceThreads();

  const animatedWidth = useRef(new Animated.Value(isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH)).current;
  const slideAnim = useRef(new Animated.Value(-EXPANDED_WIDTH)).current;

  const { isLTI, ltiLaunchUrl } = useAppConfig();

  const handleExternalPress = () => {
    if (isLTI && ltiLaunchUrl) {
      window.open(ltiLaunchUrl, "_blank");
    }
  };

  useEffect(() => {
    if (isLarge) {
      Animated.timing(animatedWidth, {
        toValue: isSidebarCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        duration: 300,
        easing: Easing.inOut(Easing.ease),
        useNativeDriver: false,
      }).start();
    } else {
      Animated.timing(slideAnim, {
        toValue: isSidebarCollapsed ? -EXPANDED_WIDTH : 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  }, [isLarge, isSidebarCollapsed, animatedWidth]);

  const handleWorkspacePress = (id: string, action: (id: string) => void) => {
    if (!isLarge) {
      setIsSidebarCollapsed(true);
    }
    action?.(id);
  };

  const renderWorkspaceSection = (label: string, workspaces: WorkspaceSidebarWorkspace[]) => {
    if (!workspaces.length) return null;

    const shouldShowLabel = !isSidebarCollapsed && label !== "GENERAL";

    return (
      <View style={styles.sectionCards}>
        {shouldShowLabel && <Text style={styles.sectionLabel}>{label}</Text>}
        {workspaces.map((workspace) => (
          <View key={workspace.id} style={styles.cardWrapper}>
            <WorkspaceCard
              {...workspace}
              isActive={workspace.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(workspace.id, onWorkspacePress)}
              onThreadsPress={() => handleWorkspacePress(workspace.id, onWorkspaceThreadsPress)}
            />
          </View>
        ))}
      </View>
    );
  };

  return (
    <View style={styles.content}>
      <Animated.View
        style={[
          styles.container,
          !isLarge && styles.containerSmall,
          isLarge ? { width: animatedWidth } : { width: EXPANDED_WIDTH, transform: [{ translateX: slideAnim }] },
        ]}>
        <View style={styles.logoSection}>
          <AxioLogo height={28} />
          {isLarge ? (
            <Tooltip
              content={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
              placement="right"
              horizontalOffset={5}
              showArrow={false}>
              <CustomButton
                styleType="secondaryLightened"
                size="s"
                style={styles.sidebarIcon}
                leftIcon={
                  isSidebarCollapsed ? (
                    <PanelLeft size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />
                  ) : (
                    <PanelLeftClose size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />
                  )
                }
                onHover={setIsHovered}
                onPress={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                accessibilityLabel="Toggle sidebar"
                accessibilityHint="Toggle the sidebar visibility"
              />
            </Tooltip>
          ) : (
            <CustomButton
              styleType="secondaryLightened"
              size="s"
              style={styles.sidebarIcon}
              leftIcon={<X size={20} color={isHovered ? colors.grey[700] : colors.grey[500]} />}
              onPress={() => setIsSidebarCollapsed(true)}
              accessibilityLabel="Close sidebar"
              accessibilityHint="Close the sidebar"
            />
          )}
        </View>

        <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
          <View style={styles.sectionCards}>
            <View style={styles.cardWrapper}>
              <WorkspaceCard
                {...homeWorkspace}
                isActive={homeWorkspace.id === activeWorkspaceId}
                onPress={() => handleWorkspacePress(homeWorkspace.id, onWorkspacePress)}
              />
            </View>
          </View>
          {renderWorkspaceSection("GENERAL", generalWorkspaces)}
          {renderWorkspaceSection("COURSE ASSISTANTS", courseWorkspaces)}
        </ScrollView>

        <View style={styles.footer}>
          {todoWorkspaces.map((item) => (
            <WorkspaceCard
              key={item.id}
              {...item}
              isActive={item.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(item.id, onWorkspacePress)}
            />
          ))}
          {settingsWorkspaces.map((item) => (
            <WorkspaceCard
              key={item.id}
              {...item}
              isActive={item.id === activeWorkspaceId}
              onPress={() => handleWorkspacePress(item.id, onWorkspacePress)}
            />
          ))}
        </View>
        <View style={styles.footer}>
          <WorkspaceCard title="Back to courses" type="backToCourses" onPress={handleExternalPress} />
        </View>
      </Animated.View>
      {/* Backdrop */}
      {!isSidebarCollapsed && !isLarge && (
        <View style={styles.backdrop} onPointerDown={() => setIsSidebarCollapsed(true)} />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    flexDirection: "row",
    position: "relative",
    zIndex: 100,
  },
  container: {
    width: 272,
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    justifyContent: "space-between",
    backgroundColor: colors.white,
  },
  containerSmall: {
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    zIndex: 100,
    // @ts-expect-error - works for web
    height: "100vh",
  },
  backdrop: {
    position: "absolute",
    // @ts-expect-error - works for web
    width: "100vw",
    // @ts-expect-error - works for web
    height: "100vh",
    backgroundColor: "#00000080",
    zIndex: 99,
  },
  logoSection: {
    height: 57,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#fff",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sidebarIcon: {
    borderRadius: "50%",
  },
  scrollArea: {
    flexGrow: 1,
  },
  scrollContent: {
    padding: spacing.xs,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: "600",
    fontFamily: "Inter",
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: colors.grey[500],
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  sectionCards: {},
  cardWrapper: {
    gap: 4,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    padding: spacing.xs,
  },
});

export default WorkspaceSidebar;
